import { WSCard, WSList } from "@wingspanhq/fe-component-library";
import { RouteComponentProps } from "react-router-dom";
import { usePayerTaxForm } from "../../../../../query/taxForm/queries/usePayerTaxForm";
import { WSQueries } from "../../../../../query/WSQuery";
import { FormEditRecipient } from "../../../components/FormEditRecipient";

import { VerificationStatus } from "@wingspanhq/payments/dist/interfaces";
import { useEffect, useRef, useState } from "react";
import { RecipientVerificationFailed } from "../../../components/RecipientVerificationFailed";
import { RecipientVerificationProgress } from "../../../components/RecipientVerificationProgress";
import { selectorPayerTaxFormDefaultStatus } from "../../../selectors/selectorPayerTaxFormDefaultStatus";
import { useModalUpdateStatus } from "../RouteTaxFormDetails/ModalUpdateStatus";
import { useModalCancelEdit } from "../../../components/ModalCancelEdit";
import { useTaxFormSubmissionParamsIds } from "../../../paths";
import { EditLayout } from "../../../components/EditLayout";
import { useGoBackOrToParent } from "../../../../../utils/goToParentRoute";
import { getTaxFormExtendedBySubmission } from "../../../utils/getTaxFormExtendedBySubmission";
import { TaxFormValidationAlert } from "../RouteTaxFormDetails/TaxFormValidationAlert";
import { useQueriesOn } from "../../../../../shared/hooks/useQueriesOn";

export const RouteTaxFormEditRecipient: React.FC<RouteComponentProps> = () => {
  const { taxFormId, submissionId } = useTaxFormSubmissionParamsIds();
  const onFinish = useGoBackOrToParent();

  const queryPayerTaxForm = usePayerTaxForm(taxFormId);

  const warningRef = useRef<HTMLElement>(null);

  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [isVerificationFailed, setIsVerificationFailed] = useState(false);
  const cancelModal = useModalCancelEdit();
  const updateStatusModal = useModalUpdateStatus();

  const onVerificationFailed = () => {
    setVerificationInProgress(false);
    setIsVerificationFailed(true);
    warningRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onVerificationPending = () => {
    setVerificationInProgress(true);
  };

  useEffect(() => {
    if (verificationInProgress) {
      const interval = setInterval(async () => {
        const taxForm = await queryPayerTaxForm.refetchData();
        if (taxForm) {
          const verificationStatus = selectorPayerTaxFormDefaultStatus(taxForm);

          if (verificationStatus === VerificationStatus.Verified) {
            onFinish();
          } else if (verificationStatus === VerificationStatus.Failed) {
            onVerificationFailed();
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [onFinish, queryPayerTaxForm, verificationInProgress]);

  useQueriesOn(
    "successAll",
    ({ data: payerTaxForm }) => {
      const verificationStatus =
        selectorPayerTaxFormDefaultStatus(payerTaxForm);

      if (verificationStatus === VerificationStatus.Failed) {
        onVerificationFailed();
      }
    },
    queryPayerTaxForm
  );

  return (
    <EditLayout title="Edit contractor details" onBack={onFinish}>
      <WSQueries
        queries={{
          queryPayerTaxForm
        }}
      >
        {({ queryPayerTaxFormData }) => {
          const taxForm = getTaxFormExtendedBySubmission(
            queryPayerTaxFormData,
            submissionId
          );

          return (
            <WSList gap="2XL">
              {verificationInProgress ? (
                <WSCard
                  divider
                  header="Verifying recipient taxpayer identification number"
                >
                  <RecipientVerificationProgress
                    ms={10000}
                    onComplete={onFinish}
                  />
                </WSCard>
              ) : (
                <>
                  {isVerificationFailed && (
                    <WSCard divider header="Verification failed">
                      <RecipientVerificationFailed
                        elementRef={warningRef}
                        taxForm={taxForm}
                      />
                    </WSCard>
                  )}

                  <TaxFormValidationAlert taxForm={taxForm} />

                  <FormEditRecipient
                    key={`key-for-reset-${taxForm.updatedAt}`}
                    isEdit={true}
                    taxForm={taxForm}
                    onCancel={async () => {
                      const result = await cancelModal.open();
                      if (result) {
                        onFinish();
                      }
                    }}
                    onBack={async () => {
                      onFinish();
                    }}
                    onSuccess={async responseTaxForm => {
                      if (
                        responseTaxForm &&
                        responseTaxForm?.status !== taxForm.status
                      ) {
                        await updateStatusModal.open({
                          taxForm: responseTaxForm
                        });
                      }

                      const verificationStatus =
                        selectorPayerTaxFormDefaultStatus(responseTaxForm);

                      if (verificationStatus === VerificationStatus.Verified) {
                        onFinish();
                      } else if (
                        verificationStatus === VerificationStatus.Failed
                      ) {
                        onVerificationFailed();
                      } else {
                        onVerificationPending();
                      }
                    }}
                  />
                </>
              )}
            </WSList>
          );
        }}
      </WSQueries>
    </EditLayout>
  );
};
